import { observer } from 'mobx-react'
import { useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type {
  Answers,
  AnswerValue,
  GroupItem,
  QuestionsScreen as QuestionsScreenType,
  ScalarAnswerValue,
} from '@/types/dataCollection'
import { ScreenHeader } from './ScreenHeader'
import { ScalarQuestionField } from './ScalarQuestionField'
import { GroupField } from './GroupField'
import {
  applyDefaultFrom,
  buildInitialValues,
  buildSubmitAnswers,
  buttonsSelectOf,
  defaultFromTargetIds,
  derivedTargetIds,
  isGroupQuestion,
  isQuestionVisible,
  resolveLabel,
  suggestedNumberValue,
  validateScreen,
} from './helpers'
import type { ValidationTexts } from './helpers'
import { HelpSection } from './HelpSection'

interface QuestionsScreenProps {
  screen: QuestionsScreenType
  savedAnswers: Answers
  /** Saved `defaultFrom` targets known to hold a computed value (see `derivedIds` on submit). */
  derivedAnswers: ReadonlySet<string>
  /** Rendered under the screen header, e.g. the retry notice. */
  notice?: ReactNode
  submitting: boolean
  canGoBack: boolean
  onBack: () => void
  /** `derivedIds`: this screen's `defaultFrom` targets whose value was computed, not typed. */
  onSubmit: (answers: Answers, derivedIds: string[]) => void
}

export const QuestionsScreen = observer(
  ({
    screen,
    notice,
    savedAnswers,
    derivedAnswers,
    submitting,
    canGoBack,
    onBack,
    onSubmit,
  }: QuestionsScreenProps) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { language } = store.TranslationsState
    const { dataCollectionPage: trans } = store.TranslationsState.translations

    const initialValues = useMemo<Answers>(
      () => buildInitialValues(screen, savedAnswers, derivedAnswers),
      [screen, savedAnswers, derivedAnswers]
    )

    const [values, setValues] = useState<Answers>(initialValues)
    // `defaultFrom` targets the user typed into. A saved value not known to be derived counts as
    // typed, so it survives later visits; the cascade clears the flag when it refills the field.
    const [overridden, setOverridden] = useState<Set<string>>(
      () =>
        new Set(
          defaultFromTargetIds(screen).filter(
            (id) => savedAnswers[id] !== undefined && !derivedAnswers.has(id)
          )
        )
    )
    // Error keys (question id, or group.index.inner) whose feedback is shown. A field joins once
    // the user changed or left it, so a freshly opened screen does not light up red everywhere.
    const [touched, setTouched] = useState<Set<string>>(() => new Set())

    const resolved: Answers = { ...savedAnswers, ...values }

    const validationTexts: ValidationTexts = {
      required: trans.errors.required,
      min: trans.errors.min,
      max: trans.errors.max,
      maxLength: trans.errors.maxLength,
      minItems: trans.errors?.minItems ?? 'Please add at least {minItems}',
      maxItems: trans.errors?.maxItems ?? 'You can add at most {maxItems}',
      iban: trans.errors?.iban ?? 'Please enter a valid IBAN',
      lessThan: trans.errors?.lessThan ?? 'Value must be less than {label}',
    }

    // Validated live on every change so cross-field rules (`lessThan`) light up the constrained
    // field as soon as either side changes; only touched fields display their message.
    const allErrors = validateScreen(screen, resolved, validationTexts, language)
    const errors = Object.fromEntries(Object.entries(allErrors).filter(([key]) => touched.has(key)))
    const isValid = Object.keys(allErrors).length === 0

    const markTouched = (keys: string[]) =>
      setTouched((prev) => {
        if (keys.every((key) => prev.has(key))) return prev
        const next = new Set(prev)
        for (const key of keys) next.add(key)
        return next
      })

    // Applies a change and re-evaluates every `defaultFrom` suggestion that depends on it
    // (cascading through suggestions that feed other suggestions). Refilled targets are derived
    // again; a target the user edited directly becomes overridden.
    const updateValues = (patch: Answers, editedId?: string) => {
      const next = { ...values, ...patch }
      const derived = applyDefaultFrom(screen, resolved, { ...savedAnswers, ...next })
      setValues({ ...next, ...derived })

      const derivedIds = Object.keys(derived)
      const override = editedId !== undefined && defaultFromTargetIds(screen).includes(editedId)
      if (!override && derivedIds.length === 0) return
      setOverridden((prev) => {
        const nextSet = new Set(prev)
        if (override) nextSet.add(editedId)
        for (const id of derivedIds) nextSet.delete(id)
        return nextSet
      })
    }

    const setScalar = (id: string, value: AnswerValue) => {
      updateValues({ [id]: value }, id)
      markTouched([id])
    }

    const setGroupItems = (id: string, items: GroupItem[]) => updateValues({ [id]: items })

    const handleSubmit = () => {
      if (!isValid) {
        markTouched(Object.keys(allErrors))
        return
      }
      onSubmit(buildSubmitAnswers(screen, resolved), derivedTargetIds(screen, resolved, overridden))
    }

    const visibleQuestions = screen.questions.filter((q) => isQuestionVisible(q, resolved))
    // A decision screen: the options are the actions, clicking one submits the screen.
    const buttonsSelect = buttonsSelectOf({ ...screen, questions: visibleQuestions })

    return (
      <div className="page-container mobile-m-0">
        <div className="layout__content-container-with-scroll">
          <ScreenHeader title={screen.title} description={screen.description} icon={screen.icon} />
          {notice}

          {buttonsSelect === undefined &&
            visibleQuestions.map((question) =>
              isGroupQuestion(question) ? (
                <GroupField
                  key={question.id}
                  question={question}
                  items={(resolved[question.id] as GroupItem[]) ?? []}
                  errors={errors}
                  onItemsChange={(items) => setGroupItems(question.id, items)}
                  onTouched={(key) => markTouched([key])}
                  addFallback={trans.group?.addItem ?? 'Add another'}
                  removeLabel={trans.group?.removeItem ?? 'Remove'}
                />
              ) : (
                <ScalarQuestionField
                  key={question.id}
                  question={question}
                  value={resolved[question.id] as ScalarAnswerValue | undefined}
                  error={errors[question.id]}
                  onChange={(value) => setScalar(question.id, value)}
                  onBlur={() => markTouched([question.id])}
                  suggestedValue={
                    question.type === 'number'
                      ? suggestedNumberValue(question, resolved)
                      : undefined
                  }
                />
              )
            )}

          {screen.help && <HelpSection help={screen.help} />}
        </div>

        {buttonsSelect ? (
          <div className="layout__content-container-bottom-fixed">
            <div
              className="data-collection__choice-buttons"
              role="group"
              aria-label={resolveLabel(buttonsSelect.label, language)}
            >
              {buttonsSelect.options.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => onSubmit({ [buttonsSelect.id]: option.value }, [])}
                  disabled={submitting}
                  className={option.emphasis === 'primary' ? undefined : 'button--secondary'}
                  dataTestid={`data-collection-choice-${option.value}`}
                >
                  {submitting && <LoadingSpinner inButton />}
                  <span
                    className={cx(
                      'generic__button-text',
                      submitting && 'generic__button-text--loading'
                    )}
                  >
                    {resolveLabel(option.label, language)}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="layout__content-container-bottom-fixed">
            {canGoBack ? (
              <Button
                onClick={onBack}
                disabled={submitting}
                className="button--secondary data-collection__back"
                dataTestid="data-collection-back"
              >
                {trans.back}
              </Button>
            ) : (
              !isMobile && <div className="layout__content-container-bottom-left" />
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !isValid}
              dataTestid="data-collection-next"
            >
              {submitting && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  submitting && 'generic__button-text--loading'
                )}
              >
                {trans.next}
              </span>
            </Button>
          </div>
        )}
        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>
    )
  }
)
